import http from "@/helper/http";
import api from "@/helper/api";

function unwrap(response) {
  const body = response?.data?.data;
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new Error("Invalid Insights response envelope.");
  }
  return body;
}

function normalizeStore(store) {
  return {
    ...store,
    id: String(store.id),
    canViewProfitability:
      store.can_view_profitability === true ||
      store.profitability_access === true,
    profitabilityAccess:
      store.can_view_profitability === true ||
      store.profitability_access === true,
  };
}

function normalizeProfitability(item) {
  return {
    ...item,
    canViewProfitability:
      item.can_view_profitability === true ||
      item.profitability_access === true,
    profitabilityAccess:
      item.can_view_profitability === true ||
      item.profitability_access === true,
    profitabilityBreakdown:
      item.profitability_breakdown ||
      item.profitability_calculation ||
      null,
    profitabilityCalculation:
      item.profitability_breakdown ||
      item.profitability_calculation ||
      null,
  };
}

function commonResponse(response) {
  const body = unwrap(response);
  if (
    !Array.isArray(body.stores) ||
    !Array.isArray(body.per_store) ||
    !Array.isArray(body.by_currency) ||
    !body.filters ||
    !body.generated_at
  ) {
    throw new Error("Invalid Insights report response.");
  }
  return {
    raw: body,
    stores: body.stores.map(normalizeStore),
    filters: body.filters,
    generatedAt: body.generated_at,
    dataFreshness: body.data_freshness,
    limits: body.limits,
    canViewProfitability:
      body.can_view_profitability === true ||
      body.profitability_access === true,
    profitabilityAccess:
      body.can_view_profitability === true ||
      body.profitability_access === true,
    profitabilityBreakdown:
      body.profitability_breakdown ||
      body.profitability_calculation ||
      null,
    profitabilityCalculation:
      body.profitability_breakdown ||
      body.profitability_calculation ||
      null,
    perStore: body.per_store.map(normalizeProfitability),
    byCurrency: body.by_currency.map(normalizeProfitability),
  };
}

export function adaptDiscovery(response) {
  const body = unwrap(response);
  if (
    !Array.isArray(body.stores) ||
    !Array.isArray(body.groups) ||
    !body.limits ||
    !Array.isArray(body.limits.supported_intervals) ||
    !Array.isArray(body.limits.supported_metrics) ||
    !Array.isArray(body.limits.supported_product_joins)
  ) {
    throw new Error("Invalid Insights discovery response.");
  }
  const rawLimits = body.limits;
  return {
    stores: body.stores.map(normalizeStore),
    groups: body.groups,
    limits: {
      ...rawLimits,
      maxStores: Number(rawLimits.max_stores),
      maxRangeDays: Number(rawLimits.max_range_days),
    },
    intervals: rawLimits.supported_intervals,
    metrics: rawLimits.supported_metrics,
    productJoins: rawLimits.supported_product_joins.map((value) =>
      String(value).toLowerCase(),
    ),
  };
}

export function adaptSummary(response) {
  return commonResponse(response);
}

export function adaptTrends(response) {
  const result = commonResponse(response);
  result.series = result.perStore;
  return result;
}

function matchedProductGroup(group) {
  return {
    ...group,
    id: `matched-${group.key}`,
    label:
      group.product_name ||
      group.per_store?.[0]?.product?.name ||
      group.display_value,
    status: "matched",
    matchKey: group.key,
    perStore: group.per_store.map(normalizeProductStoreMetrics),
    byCurrency: (group.by_currency || []).map(normalizeProductCurrencyMetrics),
    byUnit: normalizeProductUnitPartitions(group),
    conflicts: [],
    warnings: [],
  };
}

export function normalizeProductUnitPartitions(value = {}) {
  const source =
    value.by_unit ||
    value.quantity_sold_by_unit ||
    value.unit_partitions ||
    value.metrics?.by_unit ||
    value.metrics?.quantity_by_unit ||
    value.metrics?.quantity_sold_by_unit;
  if (Array.isArray(source)) {
    return source
      .map((partition) => ({
        unit:
          partition.unit ||
          partition.quantity_unit ||
          partition.quantity_unit_code,
        quantitySold:
          partition.quantity_sold ??
          partition.quantity ??
          partition.metrics?.quantity_sold ??
          partition.value,
      }))
      .filter(
        (partition) =>
          Boolean(partition.unit) && partition.quantitySold != null,
      );
  }
  if (source && typeof source === "object") {
    return Object.entries(source).map(([unit, quantitySold]) => ({
      unit,
      quantitySold:
        typeof quantitySold === "object"
          ? quantitySold.quantity_sold ??
            quantitySold.quantity ??
            quantitySold.value
          : quantitySold,
    }));
  }
  const unit =
    value.unit ||
    value.quantity_unit ||
    value.quantity_unit_code ||
    value.product?.quantity_unit ||
    value.product?.quantity_unit_code ||
    value.metrics?.quantity_unit;
  const quantitySold =
    value.quantity_sold ?? value.metrics?.quantity_sold;
  return unit && quantitySold != null ? [{ unit, quantitySold }] : [];
}

function normalizeProductStoreMetrics(item) {
  return {
    ...item,
    unitPartitions: normalizeProductUnitPartitions(item),
  };
}

function normalizeProductCurrencyMetrics(item) {
  return {
    ...item,
    unitPartitions: normalizeProductUnitPartitions(item),
  };
}

export function adaptProductComparisonData(body) {
  if (
    !Array.isArray(body.stores) ||
    !Array.isArray(body.groups) ||
    !Array.isArray(body.unmatched_products) ||
    !Array.isArray(body.conflicts) ||
    !Array.isArray(body.warnings) ||
    !Array.isArray(body.by_currency) ||
    !body.page ||
    !body.filters ||
    !body.generated_at ||
    !body.data_freshness ||
    !body.limits
  ) {
    throw new Error("Invalid Insights product comparison response.");
  }
  const unmatched = body.unmatched_products.map((product) => ({
    ...product,
    id: `unmatched-${product.store_id}-${product.id}`,
    label: product.name,
    status:
      product.reason === "ambiguous_join_key" ? "ambiguous" : "unmatched",
    matchKey:
      product.normalized_key ||
      product[body.filters.join_by.toLowerCase()] ||
      "",
    perStore: [
      normalizeProductStoreMetrics({
        store_id: product.store_id,
        product,
        currency: product.currency,
        metrics: product.metrics,
        by_unit: product.by_unit,
        unit: product.unit,
        quantity_unit: product.quantity_unit,
        quantity_unit_code: product.quantity_unit_code,
      }),
    ],
    conflicts: [],
    warnings: [],
  }));
  const conflicts = body.conflicts.map((conflict) => ({
    ...conflict,
    id: `conflict-${conflict.key}`,
    label: conflict.key,
    status: "conflict",
    matchKey: conflict.key,
    perStore: [],
    conflicts: [conflict],
    warnings: [],
  }));
  return {
    raw: body,
    stores: body.stores.map(normalizeStore),
    filters: body.filters,
    generatedAt: body.generated_at,
    dataFreshness: body.data_freshness,
    limits: body.limits,
    warnings: body.warnings,
    byCurrency: body.by_currency.map(normalizeProductCurrencyMetrics),
    byUnit: normalizeProductUnitPartitions(body),
    page: body.page,
    groups: [
      ...body.groups.map(matchedProductGroup),
      ...unmatched,
      ...conflicts,
    ],
  };
}

export function adaptProductComparison(response) {
  return adaptProductComparisonData(unwrap(response));
}

export async function fetchInsightsDiscovery(signal) {
  return adaptDiscovery(await http.get(api.insights_discovery, { signal }));
}

export async function fetchInsightsSummary(payload, signal) {
  return adaptSummary(await http.post(api.insights_summary, payload, { signal }));
}

export async function fetchInsightsTrends(payload, signal) {
  return adaptTrends(await http.post(api.insights_trends, payload, { signal }));
}

export async function fetchInsightsProductComparison(payload, signal) {
  const response = await http.post(api.insights_product_comparison, payload, {
    signal,
  });
  if (response.status === 202) {
    return { asyncTask: unwrap(response) };
  }
  return adaptProductComparison(response);
}

export function insightsErrorMessage(error) {
  if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return "";
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    (typeof error?.response?.data?.error === "string"
      ? error.response.data.error
      : "") ||
    (String(error?.message || "").startsWith("Invalid Insights")
      ? error.message
      : "") ||
    "Insights could not be loaded. Please try again."
  );
}
